'use-strict';

import angular, { IHttpService } from 'angular';
import type { Filter } from 'mongodb';
import { TradeIn } from 'src/app/@shared-types/vehicle/tradein';
import { PermissionsService } from '../../components/authorization/permissions.service';
import {
  VehicleHandoverRequest,
  VehicleReservation,
  VehicleReservationRequest,
  VehicleSellRequest,
} from '../../src/app/@shared-types/vehicle/availability';
import { ApiService } from '../../src/app/shared/services/api';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';
import { UsersService } from '../users/users.service';
import { VehicleLockRequest } from '../../src/app/@shared-types/vehicle/lock';
import { VehicleLock } from '../../src/app/@shared-types/vehicle/vehicle';
import { AutoboardVehicle } from '../../src/app/@shared-types/vehicle';
import { DeepPartial } from '../../src/app/@shared-types';
import { Bodycode, MissingBodycode } from '../../src/app/@shared-types/vehicle/bodycode';
import { EExternalAPI, PossibleImports } from '../../src/app/@shared-types/api';
import { ModelData } from '../../src/app/@shared-types/vehicle/model';
import { VehicleUpdateResult } from '../../src/app/@shared-types/vehicle/requests';
import { Vehicle } from '../../src/app/shared/models/vehicle/vehicle';

export class SalesAPIService {
  private usersService: UsersService;

  private $http: IHttpService;

  private toastService: ToastService;

  private permissionsService: PermissionsService;

  private apiService: ApiService;

  private vehicle: Vehicle;

  constructor(
    $http: IHttpService,
    toastService: ToastService,
    usersService: UsersService,
    permissionsService: PermissionsService,
    apiService: ApiService,
  ) {
    'ngInject';

    this.$http = $http;
    this.toastService = toastService;
    this.usersService = usersService;
    this.permissionsService = permissionsService;
    this.apiService = apiService;

    this.vehicle = new Vehicle(this.apiService);
  }

  private handleVehicleUpdateResult = (vehicleUpdate: VehicleUpdateResult): VehicleUpdateResult => {
    if (vehicleUpdate.errors?.length) {
      this.toastService.toast(ToastType.ERROR, 'Fahrzeug wurde nicht gespeichert');
    } else if (vehicleUpdate.warnings?.length) {
      this.toastService.toast(ToastType.WARNING, 'Fahrzeug wurde gespeichert, aber es sind neue Warnungen vorhanden.');
    } else {
      this.toastService.toast(ToastType.SUCCESS, 'Fahrzeug wurde gespeichert.');
    }
    return vehicleUpdate;
  };

  private handleFailedRequest = (e: Error): VehicleUpdateResult => {
    console.error(e);
    this.toastService.toast(ToastType.ERROR, 'Fehler beim Speichern des Fahrzeugs');
    return {};
  };

  public getVehicle = async (
    vehicleId: string,
  ): Promise<AutoboardVehicle> => this.apiService.get<AutoboardVehicle>(`/api/vehicles/vehicles/${vehicleId}`);

  public postReservation = async (
    reservationRequest: VehicleReservationRequest,
  ): Promise<VehicleUpdateResult> => this.apiService.post<VehicleUpdateResult>('/api/vehicles/reservation', reservationRequest)
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public deleteReservation = async (
    vehicleId: string,
  ): Promise<VehicleUpdateResult> => this.apiService.delete<VehicleUpdateResult>(`/api/vehicles/reservation/${vehicleId}`)
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public postSale = async (
    sellRequest: VehicleSellRequest,
  ): Promise<VehicleUpdateResult> => this.apiService.post<VehicleUpdateResult>('/api/vehicles/sell', sellRequest)
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public deleteSale = async (
    vehicleId: string,
  ): Promise<VehicleUpdateResult> => this.apiService.delete<VehicleUpdateResult>(`/api/vehicles/sell/${vehicleId}`)
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public postVehicleLock = async (
    vehicleLockRequest: VehicleLockRequest,
  ): Promise<VehicleLock> => this.apiService.post<VehicleLock>('/api/vehicles/lock', vehicleLockRequest);

  public deleteVehicleLock = async (
    vehicleId: string,
  ): Promise<void> => this.apiService.delete<void>(`/api/vehicles/lock/${vehicleId}`);

  public putVehicle = async (
    vehicle: Partial<AutoboardVehicle> | DeepPartial<AutoboardVehicle>,
    forceUpdate?: boolean,
    dummyVehicle?: boolean,
  ): Promise<VehicleUpdateResult> => this.apiService.put<VehicleUpdateResult>('/api/vehicles/vehicle', { vehicle, force: forceUpdate || false, dummyVehicle: dummyVehicle || false })
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public getBodycodes = (): Promise<Bodycode[]> => this.apiService.get<Bodycode[]>('/api/vehicles/bodycodes');

  public getMissingBodycodes = (): Promise<MissingBodycode[]> => this.apiService.get<MissingBodycode[]>('/api/vehicles/missing-bodycodes');

  public putBodycode = async (
    bodycode: Partial<Bodycode> | DeepPartial<Bodycode>,
  ): Promise<void> => this.apiService.put<void>('/api/vehicles/bodycode', bodycode);

  public postBodycode = async (
    bodycode: Partial<Bodycode> | DeepPartial<Bodycode>,
  ): Promise<string> => this.apiService.post<string>('/api/vehicles/bodycode', bodycode);

  public deleteBodycode = async (
    bodycodeId: string,
  ): Promise<void> => this.apiService.delete<void>(`/api/vehicles/bodycodes/${bodycodeId}`);

  public getUpcomingReservations = async (): Promise<VehicleReservation[]> => this.apiService
    .get<VehicleReservation[]>('/api/vehicles/reservation/upcoming');

  public postReactivateVehicle = async (
    vehicleId: string,
    updateData: DeepPartial<AutoboardVehicle>,
  ): Promise<void> => this.apiService.post<void>(`/api/vehicles/reactivate/${vehicleId}`, updateData);

  public getTradeIns = async (): Promise<TradeIn[]> => this.apiService.get<TradeIn[]>('/api/vehicles/trade-ins');

  public deleteTradeIn = async (tradeInId: number): Promise<void> => this.apiService.delete<void>(`/api/vehicles/trade-ins/${tradeInId}`);

  public postTradeIn = async (tradeIn: DeepPartial<TradeIn>): Promise<number> => this.apiService
    .post<number>('/api/vehicles/trade-ins', tradeIn);

  public postHandover = async (
    handoverData: VehicleHandoverRequest,
  ): Promise<VehicleUpdateResult> => this.apiService.post<VehicleUpdateResult>('/api/vehicles/handover', handoverData)
    .then((res) => this.handleVehicleUpdateResult(res))
    .catch((e) => this.handleFailedRequest(e));

  public getImportOptions = async (vin: string): Promise<PossibleImports> => this.apiService
    .get<PossibleImports>(`/api/vehicles/import-options/${vin}`);

  public importVehicleFromSchwacke = (vin: string): Promise<string> => this.apiService
    .get<string>(`/api/external-api/schwacke/vehicle/${vin}`);

  public importVehicleFromDAT = (vin: string): Promise<string> => this.apiService
    .get<string>(`/api/external-api/dat/vehicle/${vin}`);

  public importUSVehicle = (vin: string): Promise<string> => this.apiService
    .get<string>(`/api/external-api/us-vin/vehicle/${vin}`);

  public importLocoSoftDbVehicle = (vin: string): Promise<string> => this.apiService
    .get<string>(`/api/external-api/loco-soft-db/vehicle/${vin}`);

  public getExportCount = (filter: unknown): Promise<number> => this.apiService
    .post<number>('/api/export/count', { filter });

  public getModelDataFromAPI = (
    api: EExternalAPI,
    vin: string,
  ): Promise<ModelData> => this.apiService
    .get<ModelData>(`/api/external-api/${api}/model-data/${vin}`);

  public getBodycode = async (filter?: Filter<AutoboardVehicle>): Promise<Bodycode> => {
    const query = filter ? `?filter=${JSON.stringify(filter)}` : '';
    return this.apiService.get<Bodycode>(`/api/vehicles/bodycode${query}`);
  };

  public getVehiclesWithOnlineErrors = (): Promise<AutoboardVehicle[]> => this.vehicle.getAll({
    'apiData.exportErrors.0': { $exists: true },
  });

  /** Get the transfer cost for a car defined by either make or model */
  public getTransferCost = (
    make: string,
    model: string,
  ): Promise<{ transferCosts: number | null }> => this.apiService
    .get(`/api/vehicles/transfer-costs/${make}/${model}`);

  // CAR API's

  // /**
  //  * Get all details for a single car defined by ID.
  //  * Get saved data for the car as well data provided by the bodycode.
  //  *
  //  * @param {int} carId
  //  */
  // apiGetCar(carId) {
  //   return new Promise((resolve, reject) => {
  //     let car = null;

  //     this.$http
  //       .get(`${API_URL}/api/vehicles/vehicle/${carId}`)
  //       .then((res) => {
  //         car = res.data;
  //         return this.$http
  //           .get(`${API_URL}/api/vehicles/bodycodes/${encodeURIComponent(car.code)}`);
  //       })
  //       .then((res) => resolve({
  //         car,
  //         carDetail: res.data,
  //       }))
  //       .catch((err) => {
  //         this.apiError('Fahrzeugdaten konnten nicht abgerufen werden.');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Ask server to check if the car with the provided VIN is already saved.
  //  * If not, check if VIN can be found at any API for a convenvient import.
  //  *
  //  * @param {string} vin
  //  * @returns {Promise <Object>}
  //  */
  // apiGetImportOptions(vin) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .get(`${API_URL}/api/vehicles/import-options/${vin}`)
  //       .then((importOptions) => resolve(importOptions.data))
  //       .catch((e) => {
  //         this.apiError('Fehler beim Abrufen der Optionen.');
  //         return reject(e);
  //       });
  //   });
  // }

  // /**
  //  * Reactivate a vehicle and also update the provided data.
  //  *
  //  * @param {number} carId
  //  * @param {object} updateData
  //  * @returns {Promise <Object>}
  //  */
  // apiReactivateVehicle(carId, updateData) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .post(`${API_URL}/api/vehicles/reactivate/${carId}`, updateData)
  //       .then((reactivationResult) => resolve(reactivationResult))
  //       .catch((e) => {
  //         this.apiError('Fehler bei der Reaktivierung.');
  //         return reject(e);
  //       });
  //   });
  // }

  // /**
  //  * Update a car detail (bodycode)
  //  *
  //  * @param {Object} carDetail
  //  */
  // apiUpdateCarDetail(carDetail) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .put(`${API_URL}/api/vehicles/bodycodes`, carDetail)
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Fehler beim Speichern der Änderungen am Fahrzeug.');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Insert a new car into Autoboard.
  //  *
  //  * @param {Object} car
  //  */
  // apiInsertCar(car) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .post(`${API_URL}/api/vehicles/vehicle`, car)
  //       .then((res) => resolve(res.data))
  //       .catch((err) => {
  //         this.apiError('Fehler beim Anlegen des neuen Fahrzeugs.');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Insert a new car detail into Autoboard.
  //  *
  //  * @param {Object} carDetail
  //  */
  // apiInsertCarDetail(carDetail) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .post(`${API_URL}/api/vehicles/bodycode`, carDetail)
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Fehler beim Anlegen des neuen Fahrzeugs.');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Update a car with the provided data.
  //  *
  //  * @param {int} carId
  //  * @param {Object} changedVals
  //  */
  // apiUpdateCar(carId, changedVals) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .put(`${API_URL}/api/vehicles/vehicle`, {
  //         carId,
  //         userId: this.usersService.currentUserId(),
  //         changedVals,
  //       })
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Fehler beim Speichern der Änderungen am Fahrzeug.');
  //         return reject(err);
  //       });
  //   });
  // }

  // // LOCK API's

  // /**
  //  * Try to lock a car for current user.
  //  *
  //  * @param {int} carId
  //  * @param {string} lockMethod
  //  */
  // apiLockCar(carId, lockMethod) {
  //   return new Promise((resolve, reject) => {
  //     const data = {
  //       carId,
  //       userId: this.usersService.currentUserId(),
  //       lockMethod,
  //     };
  //     this.$http
  //       .post(`${API_URL}/api/vehicles/lock`, data)
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Das Fahrzeug kann für die Bearbeitung aktuell nicht gesperrt werden.');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Try to remove a lock from a car.
  //  *
  //  * @param {int} carId
  //  */
  // apiRemoveLock(carId) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .delete(`${API_URL}/api/vehicles/lock/${carId}`)
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Fehler beim Freigeben des Datensatzes');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Get all cars from API server
  //  *
  //  * @param {Boolean} showSoldStock
  //  * @param {Array} columns to return
  //  */
  // apiGetAllCars(showSoldStock, columns) {
  //   return new Promise((resolve, reject) => {
  //     let url = showSoldStock
  //       ? `${API_URL}/api/vehicles/sold-vehicles`
  //       : `${API_URL}/api/vehicles/vehicles`;

  //     if (columns !== undefined) {
  //       url = `${url}?columns=${encodeURIComponent(columns)}`;
  //     }

  //     this.$http.get(url)
  //       .then((response) => resolve(response.data))
  //       .catch((err) => {
  //         this.apiError('Fehler beim Abrufen der Fahrzeugdaten.');
  //         return reject(err);
  //       });
  //   });
  // }

  // // IMAGES API's

  // /**
  //  * Get all associated image for the specified `car` and saves them to it.
  //  *
  //  * @param {int} mobileId
  //  */
  // apiGetCarImages(mobileId) {
  //   return new Promise((resolve, reject) => {
  //     if (mobileId === null) return resolve([]);
  //     if (!this.permissionsService.isPermitted({
  //       method: 'GET',
  //       resource: 'VEHICLE_IMAGES',
  //     })) return resolve([]);

  //     this.$http.get(`${API_URL}/api/vehicles/vehicle-images/${mobileId}`)
  //       .then((images) => resolve(images.data))
  //       .catch((err) => {
  //         this.apiError('Die Bilder des Fahrzeugs konnten nicht abgerufen werden.');
  //         return reject(err);
  //       });
  //     return null;
  //   });
  // }

  // // SCHWACKE API's

  // apiGetSchwackeCar(schwackeId) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .get(`${API_URL}/api/vehicles/schwacke-vehicles/${schwackeId}`)
  //       .then((res) => resolve(res.data))
  //       .catch((err) => {
  //         this.apiError(err.data.errors.message);
  //         return reject(err);
  //       });
  //   });
  // }

  // apiGetTradeIns(tradeInId) {
  //   return new Promise((resolve, reject) => {
  //     const singleTradeIn = tradeInId !== undefined ? `/${tradeInId}` : '';

  //     this.$http
  //       .get(`${API_URL}/api/vehicles/trade-ins${singleTradeIn}`)
  //       .then((res) => resolve(this.prepareTradeIns(res.data)))
  //       .catch((err) => {
  //         this.apiError(err.data.errors.message);
  //         return reject(err);
  //       });
  //   });
  // }

  // apiDeleteCar(carId) {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .delete(`${API_URL}/api/vehicles/vehicle/${carId}`)
  //       .then(() => resolve())
  //       .catch((err) => {
  //         this.apiError('Fehler beim Löschen des Fahrzeugs');
  //         return reject(err);
  //       });
  //   });
  // }

  // apiGetUpcomingReservations() {
  //   return new Promise((resolve, reject) => {
  //     this.$http
  //       .get(`${API_URL}/api/vehicles/upcoming-reservations`)
  //       .then((res) => resolve(res.data))
  //       .catch((err) => {
  //         this.apiError('Fehler beim Abrufen der anstehenden Reservierungen');
  //         return reject(err);
  //       });
  //   });
  // }

  // /**
  //  * Prepares the list of tradeIns for display in frontend
  //  *
  //  * @param {Array <TradeIn>} tradeIns
  //  */
  // prepareTradeIns(tradeIns) {
  //   for (const tradeIn of tradeIns) {
  //     switch (tradeIn.state) {
  //       case 'open':
  //         tradeIn.state_description = 'Offen';
  //         break;
  //       case 'imported':
  //         tradeIn.state_description = 'Importiert';
  //         break;
  //       default:
  //         break;
  //     }
  //   }
  //   return tradeIns;
  // }

  // /**
  //  * Show an error message and log the error stack to the console
  //  *
  //  * @param {string} message
  //  */
  // apiError(message) {
  //   this.toastService.toast('error', message);
  // }
}

export default angular.module('services.salesApiService', [])
  .service('salesApiService', SalesAPIService)
  .name;
